import React, { useRef } from 'react';
import { CheckCircle, Circle } from 'lucide-react';
import { useJourneyStore, JourneyStep } from '../../store/journeyStore';

const STEP_LABELS: Record<JourneyStep, string> = {
  COMPANY_SETUP: 'Company Setup',
  VISION_WORKSHOP: 'Vision Workshop',
  BRAND_DEVELOPMENT: 'Brand Development',
  OPERATIONS_SETUP: 'Operations Setup',
  COMPLETE: 'Journey Complete'
};

export default function JourneyProgress() {
  const { currentStep, completedSteps, progress } = useJourneyStore();

  const isStepCompleted = (step: JourneyStep) => completedSteps.includes(step);
  const isStepCurrent = (step: JourneyStep) => currentStep === step;
  
  // Calculate the correct progress percentage
  const calculateProgress = () => {
    const totalSteps = Object.keys(STEP_LABELS).length - 1; // Exclude COMPLETE
    const completedCount = completedSteps.length;
    return Math.round((completedCount / totalSteps) * 100);
  };
  
  const displayProgress = calculateProgress();

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Business Journey</h2>
        <p className="text-gray-500 mt-1">Track your progress through key milestones</p>
      </div>

      <div className="relative mb-4">
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
        <p className="text-sm text-gray-500 mt-2">{displayProgress}% Complete</p>
      </div>

      <div className="space-y-4">
        {Object.entries(STEP_LABELS).map(([step, label]) => (
          <div 
            key={step}
            className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
              isStepCurrent(step as JourneyStep) ? 'bg-blue-50' : ''
            }`}
          >
            {isStepCompleted(step as JourneyStep) ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : isStepCurrent(step as JourneyStep) ? (
              <Circle className="w-5 h-5 text-blue-500" />
            ) : (
              <Circle className="w-5 h-5 text-gray-300" />
            )}
            <span className={`${
              isStepCompleted(step as JourneyStep) ? 'text-gray-500' :
              isStepCurrent(step as JourneyStep) ? 'text-blue-600 font-medium' :
              'text-gray-400'
            }`}>
              {label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}